import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft,
  Search,
  MapPin,
  Calendar,
  Clock,
  Star,
  Filter,
  ChevronRight,
  Ticket,
  Music,
  PartyPopper,
  Waves,
  Users
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const categoryIcons = {
  "Party": PartyPopper,
  "Festival": Music,
  "Day Party": Users,
  "Concert": Music,
  "Beach Event": Waves,
  "Club Night": PartyPopper,
  "default": Ticket
};

const Events = ({ user }) => {
  const navigate = useNavigate();
  const [selectedParish, setSelectedParish] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dateFilter, setDateFilter] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Fetch parishes
  const { data: parishesData } = useQuery({
    queryKey: ["parishes"],
    queryFn: async () => {
      const response = await axios.get(`${API}/events/parishes`);
      return response.data;
    }
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await axios.get(`${API}/events/categories`);
      return response.data;
    }
  });

  // Fetch events
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ["events", selectedParish, selectedCategory, dateFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedParish) params.append("parish", selectedParish);
      if (selectedCategory) params.append("category", selectedCategory);
      if (dateFilter) params.append("date_filter", dateFilter);
      params.append("limit", "20");
      
      const response = await axios.get(`${API}/events?${params.toString()}`);
      return response.data;
    }
  });

  // Fetch featured events
  const { data: featuredData } = useQuery({
    queryKey: ["featuredEvents"],
    queryFn: async () => {
      const response = await axios.get(`${API}/events/featured`);
      return response.data;
    }
  });

  const parishes = parishesData?.parishes || [];
  const categories = categoriesData?.categories || [];
  const events = eventsData?.events || [];
  const featuredEvents = featuredData?.events || [];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      weekday: "short", 
      month: "short", 
      day: "numeric" 
    });
  };

  const formatPrice = (price) => {
    if (!price) return "Free";
    return `J$${price.toLocaleString()}`;
  };

  const filteredEvents = searchQuery 
    ? events.filter(e => 
        e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.venue.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : events;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="events-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 max-w-lg mx-auto">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/lifestyle")}
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">Events</h1>
            <p className="text-xs text-muted-foreground">Discover Jamaica's hottest events</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 bg-white/5 border-white/10 text-white"
            data-testid="search-input"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
              showFilters ? "bg-emerald-500/20 text-emerald-400" : "text-muted-foreground hover:text-white"
            }`}
          >
            <Filter className="w-4 h-4" />
          </button>
        </div>

        {/* Date Filter Pills */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          {[
            { id: "today", label: "Today" },
            { id: "weekend", label: "This Weekend" },
            { id: "upcoming", label: "Upcoming" }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => setDateFilter(filter.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                dateFilter === filter.id
                  ? "bg-emerald-500 text-white"
                  : "bg-white/5 text-muted-foreground hover:bg-white/10"
              }`}
              data-testid={`filter-${filter.id}`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="space-y-3 mb-4 p-4 bg-white/5 rounded-xl animate-in fade-in duration-200">
            {/* Parish Filter */}
            <div>
              <p className="text-xs text-muted-foreground mb-2">Parish</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedParish(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    !selectedParish ? "bg-emerald-500 text-white" : "bg-white/10 text-muted-foreground"
                  }`}
                >
                  All
                </button>
                {parishes.slice(0, 6).map((parish) => (
                  <button
                    key={parish}
                    onClick={() => setSelectedParish(parish)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                      selectedParish === parish ? "bg-emerald-500 text-white" : "bg-white/10 text-muted-foreground"
                    }`}
                  >
                    {parish}
                  </button>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <p className="text-xs text-muted-foreground mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                    !selectedCategory ? "bg-emerald-500 text-white" : "bg-white/10 text-muted-foreground"
                  }`}
                >
                  All
                </button>
                {categories.slice(0, 5).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                      selectedCategory === cat ? "bg-emerald-500 text-white" : "bg-white/10 text-muted-foreground"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="px-5 max-w-lg mx-auto">
        {/* Featured Events */}
        {featuredEvents.length > 0 && !searchQuery && !selectedParish && !selectedCategory && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />
                Featured
              </h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
              {featuredEvents.map((event) => (
                <button
                  key={event.event_id}
                  onClick={() => navigate(`/events/${event.event_id}`)}
                  className="flex-shrink-0 w-64 rounded-2xl overflow-hidden bg-white/5 border border-white/10 text-left"
                  data-testid={`featured-${event.event_id}`}
                >
                  {(event.flyer_url || event.image_url) ? (
                    <img 
                      src={event.flyer_url || event.image_url} 
                      alt={event.name}
                      className="w-full h-32 object-cover"
                    />
                  ) : (
                    <div className="w-full h-32 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Ticket className="w-10 h-10 text-white/50" />
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm font-semibold text-white line-clamp-1">{event.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(event.event_date)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-emerald-400 font-medium">
                        From {formatPrice(event.starting_price)}
                      </span>
                      {event.has_early_bird && (
                        <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                          Early Bird
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* All Events */}
        <div>
          <h2 className="text-sm font-semibold text-white mb-3">
            {searchQuery ? "Search Results" : selectedParish || selectedCategory ? "Filtered Events" : "All Events"}
            <span className="text-muted-foreground ml-2">({filteredEvents.length})</span>
          </h2>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No events found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEvents.map((event) => {
                const CategoryIcon = categoryIcons[event.category] || categoryIcons.default;
                const eventImage = event.flyer_url || event.image_url;
                return (
                  <button
                    key={event.event_id}
                    onClick={() => navigate(`/events/${event.event_id}`)}
                    className="w-full flex gap-4 p-3 rounded-xl bg-white/5 border border-white/10 text-left hover:bg-white/10 transition-colors"
                    data-testid={`event-${event.event_id}`}
                  >
                    {eventImage ? (
                      <img 
                        src={eventImage} 
                        alt={event.name}
                        className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-purple-500/50 to-pink-500/50 flex items-center justify-center flex-shrink-0">
                        <CategoryIcon className="w-8 h-8 text-white/50" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white line-clamp-1">{event.name}</p>
                      
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(event.event_date)}</span>
                      </div>
                      
                      <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span className="line-clamp-1">{event.venue}, {event.parish}</span>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-semibold text-emerald-400">
                          {formatPrice(event.starting_price)}
                        </span>
                        <div className="flex items-center gap-2">
                          {event.has_early_bird && (
                            <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                              Early Bird
                            </span>
                          )}
                          {event.availability === "limited" && (
                            <span className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">
                              Limited
                            </span>
                          )}
                          {event.availability === "sold_out" && (
                            <span className="text-[10px] px-2 py-0.5 bg-white/20 text-white rounded-full">
                              Sold Out
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 self-center" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <NavBar />
    </div>
  );
};

export default Events;
